// Curated sample points along California State Route 1 (Pacific Coast
// Highway) ordered south → north. Each point sits ON the coastal CA-1
// alignment so Mapbox snaps cleanly when we use it as a routing waypoint.
//
// Why this exists: Mapbox's cycling profile (and even driving with
// "scenic" tolerance) frequently routes inland between coastal cities
// because the inland highway is faster. For trips whose corridor
// genuinely runs along the coast, the user wants to ride PCH itself.
// We force that by injecting one mid-trip waypoint from this list.

import type { LngLat } from "@/utils/polyline";

/** [lng, lat] sample points along CA-1, south → north. */
export const PCH_SAMPLES: LngLat[] = [
  [-117.700, 33.467], // Dana Point
  [-117.787, 33.542], // Laguna Beach
  [-117.929, 33.617], // Newport Beach
  [-118.193, 33.770], // Long Beach
  [-118.493, 34.010], // Santa Monica
  [-118.780, 34.034], // Malibu
  [-119.097, 34.092], // Point Mugu
  [-119.293, 34.275], // Ventura
  [-119.694, 34.420], // Santa Barbara
  [-120.227, 34.471], // Gaviota
  [-120.643, 35.143], // Pismo Beach
  [-120.892, 35.443], // Cayucos
  [-121.083, 35.564], // Cambria
  [-121.327, 35.785], // Ragged Point
  [-121.530, 36.018], // Lucia
  [-121.808, 36.273], // Big Sur
  [-121.902, 36.373], // Bixby Bridge
  [-121.923, 36.555], // Carmel
  [-121.901, 36.604], // Monterey
  [-121.787, 36.806], // Moss Landing
  [-122.030, 36.962], // Santa Cruz
  [-122.197, 37.013], // Davenport
  [-122.395, 37.255], // Pescadero
  [-122.480, 37.464], // Half Moon Bay
  [-122.487, 37.611], // Pacifica
  [-122.510, 37.760], // San Francisco (Great Hwy)
  [-122.642, 37.901], // Stinson Beach
  [-122.806, 38.066], // Point Reyes Station
  [-123.048, 38.333], // Bodega Bay
  [-123.114, 38.451], // Jenner
  [-123.450, 38.722], // Sea Ranch
  [-123.527, 38.770], // Gualala
  [-123.692, 38.909], // Point Arena
  [-123.799, 39.305], // Mendocino
  [-123.805, 39.446], // Fort Bragg
  [-123.781, 39.638], // Westport
  [-123.714, 39.870], // Leggett
  // NORTH OF LEGGETT the coast road becomes US-101 — same shoreline, different
  // shield. Added Aug 2 after Mike Sternad's email: Portland → San Francisco
  // recommended nothing coastal, because the samples simply STOPPED here and
  // the matcher correctly concluded his trip wasn't near "the coast" it knew.
  // Redwood Highway + Oregon Coast Highway, south → north:
  [-123.787, 40.030], // Richardson Grove / Avenue of the Giants south end
  [-123.808, 40.117], // Garberville (US-101)
  [-124.157, 40.598], // Fortuna
  [-124.163, 40.803], // Eureka
  [-124.147, 41.059], // Trinidad
  [-124.063, 41.546], // Klamath / Redwood NP
  [-124.202, 41.756], // Crescent City
  [-124.284, 42.052], // Brookings (OR)
  [-124.421, 42.407], // Gold Beach
  [-124.498, 42.746], // Port Orford
  [-124.408, 43.119], // Bandon
  [-124.218, 43.367], // Coos Bay
  [-124.098, 43.703], // Reedsport
  [-124.104, 43.974], // Florence
  [-124.109, 44.311], // Yachats
  [-124.053, 44.637], // Newport
  [-123.911, 44.960], // Lincoln City
  [-123.844, 45.457], // Tillamook
  [-123.961, 45.892], // Cannon Beach
  [-123.831, 46.188], // Astoria
];

/** Rough km between two LngLat (haversine). */
function distKm(a: LngLat, b: LngLat): number {
  const R = 6371;
  const dLat = ((b[1] - a[1]) * Math.PI) / 180;
  const dLng = ((b[0] - a[0]) * Math.PI) / 180;
  const lat1 = (a[1] * Math.PI) / 180;
  const lat2 = (b[1] * Math.PI) / 180;
  const x = Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(x));
}

/** [lng, lat] sample points along Michigan's Lake Michigan shore, south →
 *  north, each sitting ON the shoreline road (Blue Star Highway, Lakeshore
 *  Drive, Scenic Drive, Old US-31, M-116, M-22) rather than on the US-31
 *  expressway that parallels it a few miles inland.
 *
 *  Added Aug 1 after a West Michigan reviewer ran Holland → Traverse City
 *  with water view on and got sent inland through Grand Rapids. This is the
 *  drive the region considers definitive, and the one he named: Holland,
 *  Grand Haven, Ludington, Manistee, Beulah, Traverse City. */
export const LAKE_MICHIGAN_SAMPLES: LngLat[] = [
  [-86.744, 41.794], // New Buffalo
  [-86.274, 42.403], // South Haven
  [-86.201, 42.655], // Saugatuck / Douglas
  [-86.170, 42.880], // Holland lakeshore
  [-86.205, 42.970], // Ottawa County dunes
  [-86.228, 43.063], // Grand Haven
  // Lake Harbor and Ludington State Park REMOVED from the thread set (Aug 2):
  // both are dead-end spurs. Threading a via onto a spur forces a drive-in,
  // drive-out detour — with 14 anchors the Holland→TC coastal route ballooned
  // to 377 min, 2.48× the direct, blowing the 2× cap and vanishing from the
  // cards entirely. Corridor markers and route waypoints are different jobs.
  [-86.248, 43.234], // Muskegon
  [-86.348, 43.410], // Whitehall / Montague
  [-86.488, 43.661], // Silver Lake
  [-86.432, 43.782], // Pentwater
  [-86.453, 43.955], // Ludington
  [-86.324, 44.245], // Manistee
  [-86.236, 44.495], // Onekama / Arcadia (M-22)
  [-86.234, 44.634], // Frankfort (M-22)
  [-86.180, 44.690], // Empire / Platte (M-22)
  [-86.058, 44.811], // Glen Arbor / Sleeping Bear (M-22)
  [-85.621, 44.763], // Traverse City
];

/** Every coastline Pleasant will commit to. Checked in order; the first
 *  whose corridor matches wins, and trips match at most one coast. */
const COASTLINES: LngLat[][] = [PCH_SAMPLES, LAKE_MICHIGAN_SAMPLES];

function nearestIndex(p: LngLat, samples: LngLat[]): { idx: number; km: number } {
  let bestIdx = -1, bestKm = Infinity;
  for (let i = 0; i < samples.length; i++) {
    const k = distKm(p, samples[i]);
    if (k < bestKm) { bestKm = k; bestIdx = i; }
  }
  return { idx: bestIdx, km: bestKm };
}

/**
 * If `start` and `end` are both close to PCH (within `maxOffsetKm`),
 * return up to `maxWaypoints` waypoints sampled from the PCH alignment
 * BETWEEN them, so Mapbox routes via the coast.
 *
 * Returns [] when either endpoint isn't near PCH (so callers can no-op
 * safely on inland trips).
 */
export function pchWaypointsForCorridor(
  start: LngLat,
  end: LngLat,
  opts: { maxOffsetKm?: number; maxWaypoints?: number } = {},
): LngLat[] {
  // ENDPOINT ALLOWANCE SCALES WITH THE TRIP (Aug 2, Mike Sternad's Portland →
  // San Francisco). A fixed 30km answers "does this trip START on the coast?"
  // — the right question for SF → Half Moon Bay, the wrong one for a 630-mile
  // drive where getting to the coast is the point. Portland sits ~95km inland;
  // on a trip that long, that's nothing. An eighth of the straight-line
  // distance, floored at the old 30km so short-trip behavior is unchanged.
  const tripKm = distKm(start, end);
  const maxOffsetKm = opts.maxOffsetKm ?? Math.max(30, tripKm / 8);
  const maxWaypoints = opts.maxWaypoints ?? 2;

  for (const samples of COASTLINES) {
    const out = waypointsAlongCoast(samples, start, end, maxOffsetKm, maxWaypoints);
    if (out.length > 0) return out;
  }
  return [];
}

function waypointsAlongCoast(
  samples: LngLat[],
  start: LngLat,
  end: LngLat,
  maxOffsetKm: number,
  maxWaypoints: number,
): LngLat[] {
  const a = nearestIndex(start, samples);
  const b = nearestIndex(end, samples);
  if (a.km > maxOffsetKm || b.km > maxOffsetKm) return [];
  if (a.idx === b.idx) return [];

  const lo = Math.min(a.idx, b.idx);
  const hi = Math.max(a.idx, b.idx);
  const inner = samples.slice(lo + 1, hi);
  if (inner.length === 0) return [];

  // Sample evenly across the inner stretch.
  const n = Math.min(maxWaypoints, inner.length);
  const out: LngLat[] = [];
  for (let i = 1; i <= n; i++) {
    const t = i / (n + 1);
    out.push(inner[Math.floor(t * inner.length)]);
  }
  // Preserve travel direction (south→north vs north→south).
  if (a.idx > b.idx) out.reverse();
  return out;
}
